/**
 * Account Deactivation page for the Wellness AI web app
 * Handles account deactivation process with confirmation
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import '../styles/DeactivateAccount.css';

const DeactivateAccount = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleBackToSettings = () => {
    navigate('/settings');
  };

  const handleDeactivateRequest = () => {
    setShowConfirmation(true);
  };

  const handleConfirmDeactivation = async () => {
    if (confirmText.toLowerCase() !== 'delete my account') {
      alert('Please type "DELETE MY ACCOUNT" exactly to confirm.');
      return;
    }

    setIsLoading(true);

    try {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }

      // Update user profile to mark as deactivated
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ 
          is_active: false,
          deactivated_at: new Date().toISOString()
        })
        .eq('id', session.user.id);

      if (profileError) {
        console.error('Error deactivating profile:', profileError);
        throw profileError;
      }

      // Sign out the user
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (signOutError) {
        console.log('SignOut error during deactivation (expected):', signOutError.message);
      }

      // Show success message and redirect
      alert('Your account has been deactivated successfully. Thank you for using our service.');
      navigate('/login');

    } catch (error) {
      console.error('Error during account deactivation:', error);
      alert('There was an error deactivating your account. Please try again or contact support.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDeactivation = () => {
    setShowConfirmation(false);
    setConfirmText('');
  };

  return (
    <div className="deactivate-page">
      <div className="deactivate-container">
        <div className="deactivate-header">
          <button 
            onClick={handleBackToSettings}
            className="back-button"
            aria-label="Back to settings"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
            </svg>
            Back to Settings
          </button>
          <h1>Deactivate Account</h1>
        </div>

        {!showConfirmation ? (
          <div className="deactivate-content">
            <div className="warning-section">
              <div className="warning-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                </svg>
              </div>
              <h2>Are you sure you want to deactivate your account?</h2>
              <p>This action will have the following consequences:</p>
            </div>

            <div className="consequences-list">
              <div className="consequence-item">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                </svg>
                <span>All your conversation history will be permanently deleted</span>
              </div>
              <div className="consequence-item">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                </svg>
                <span>Your subscription will be immediately cancelled</span>
              </div>
              <div className="consequence-item">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                </svg>
                <span>Any remaining tokens will be forfeited</span>
              </div>
              <div className="consequence-item">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                </svg>
                <span>You'll need to create a new account to use our service again</span>
              </div>
            </div>

            <div className="alternative-section">
              <h3>Consider these alternatives:</h3>
              <ul>
                <li>Take a break by cancelling your subscription (you can keep your account)</li>
                <li>Contact our support team if you're having issues with the service</li>
                <li>Review our FAQ for answers to common questions</li>
              </ul>
            </div>

            <div className="action-buttons">
              <button onClick={handleDeactivateRequest} className="deactivate-button danger">
                I understand, proceed with deactivation
              </button>
              <button onClick={handleBackToSettings} className="cancel-button">
                Cancel, keep my account
              </button>
            </div>
          </div>
        ) : (
          <div className="confirmation-content">
            <h2>Final Confirmation</h2>
            <p>To confirm account deactivation, please type <strong>"DELETE MY ACCOUNT"</strong> in the box below:</p>
            
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type: DELETE MY ACCOUNT"
              className="confirmation-input"
              disabled={isLoading}
            />

            <div className="final-buttons">
              <button 
                onClick={handleConfirmDeactivation} 
                className="confirm-delete-button"
                disabled={isLoading || confirmText.toLowerCase() !== 'delete my account'}
              >
                {isLoading ? 'Deactivating...' : 'Permanently Deactivate Account'}
              </button>
              <button 
                onClick={handleCancelDeactivation} 
                className="cancel-confirmation-button"
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeactivateAccount;
